import moment from 'moment';
import libphonenumber from 'libphonenumber-js';
import { IRecipient } from '@/types/Recipient';
import { EnumPlans } from '@/enums/Plans';
import { ISession } from '@/types/Session';
import { translate } from '@/helpers/translation.helper';

export function cacheBuster(force: boolean) {
  let cachebuster: string;
  if (force) {
    cachebuster = `${Math.round(new Date().getTime() / 1000)}`;
  } else {
    cachebuster = 'init';
  }

  return cachebuster;
}

export function setGlobalProp(propChain: string[], value: any) {
  let currentPath = getGlobalHandle();
  propChain.forEach(prop => {
    currentPath = currentPath[prop];
  });
  currentPath = value;
}

export function getGlobalHandle() {
  return window as any;
}

export function initRetentionHelpers(session, handle, isV3) {
  const intercomData: any = {
    app_id: 'g2tc8sjs',
    name: session.full_name,
    email: session.email,
    mobile: session.mobile,
    user_hash: session.iio_hash,
    user_id: session.user_hash,
    created_at: moment.utc(session.user_created_at).unix(),
    role: session.user_role,
    registration_source: session.registration_source,
    registration_campaign: session.registration_campaign,
    registration_device: session.registration_device,
    achievements_level: session.achievements_level,
    language_override: session.language,
    app_mode: session.app_mode,
    is_v3: isV3 ? 'yes' : 'no',
  };

  if (document.cookie) {
    let pairs = (/(?:^|; )__utmz=([^;]*)/.exec(document.cookie) || []).slice(1).pop();
    if (pairs) {
      pairs = pairs.split('.').slice(4).join('.').split('|');
      const vals: any = {};
      for (let i = 0; i < pairs.length; i++) {
        const temp = pairs[i].split('=');
        vals[temp[0]] = temp[1];
      }
      if (vals.utmgclid || vals.utmcsr) {
        intercomData.utm_source = vals.utmgclid ? 'google' : vals.utmcsr;
        intercomData.utm_medium = vals.utmgclid ? 'cpc' : vals.utmcmd;
        intercomData.utm_campaign = vals.utmccn;
      }
    }

    pairs = /(?:^|; )hsfirstvisit=([^;|]*)/.exec(document.cookie) || [];
    if (pairs) {
      pairs = pairs.slice(1).pop();
      intercomData.hsfirstvisit = decodeURIComponent(pairs).substring(0, 100);
    }
  }

  if (session.entity_hash) {
    intercomData.company = {
      id: session.entity_hash,
      name: session.entity_name,
      created_at: moment.utc(session.entity_created_at).unix(),
      plan: session.plan,
    };
  }
  if (handle.Appcues) {
    handle.Appcues.identify(intercomData.user_id, intercomData);
  }

  if (handle.Intercom.booted) {
    handle.Intercom('update', intercomData);
  } else {
    handle.Intercom('boot', intercomData);
  }
}

export const capitalize = (text: string): string =>
  text ? text[0].toUpperCase() + text.slice(1) : '';

export const getBooleanForBackend = (value: boolean): string => {
  return value ? 'true' : '';
};

export const getDocumentUrl = (id: string, status: string): string => {
  return ['draft', 'importing'].indexOf(status) > -1
    ? `/document/edit/${id}`
    : `/document/view/${id}`;
};

export const sanitizeEmail = (email: string): string => {
  return email.toLowerCase().replace('å', 'a').replace('ä', 'a').replace('ö', 'o');
};

export const jsonToParams = (json: object, separator = '&') => {
  return Object.keys(json)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(json[key])}`)
    .join(separator);
};

export const uniqueArray = (value: string[]): string[] => {
  const set = new Set(value);
  return Array.from(set);
};

export const testMobileNumber = (number: string): boolean => {
  return libphonenumber(number)?.isValid() || false;
};

export const initialHelper = (fullName: string): string => {
  const names: string[] = fullName?.split(' ') || [];
  if (names.length === 0) {
    return '';
  }
  let initials = names[0].substring(0, 1);
  if (names.length > 1) {
    initials = initials + names[names.length - 1].substring(0, 1);
  } else {
    if (names[0].length > 1) {
      initials = names[0].substring(0, 2);
    }
  }
  return initials.toUpperCase();
};

export const fullName = (firstName: string, lastName): string => {
  return `${firstName} ${lastName}`.trim();
};

export function getRecipientById(recipientId: string, recipients: IRecipient[]): IRecipient {
  return recipients.find((recipient: IRecipient) => recipient.id === recipientId);
}

export const validateEmail = (email: string): boolean => {
  const pattern = /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
  return pattern.test(email);
};

export const getEmailDomain = (email: string): string => {
  return email.replace(/.*@/, '');
};

export function throttle(func: Function, rate: number) {
  let enabled = true;

  return function (arg?: any) {
    if (enabled) {
      func(arg);
      enabled = false;
      setTimeout(() => {
        enabled = true;
      }, rate);
    }
  };
}

export const isBetaCustomer = (entityHash: string) =>
  ['zw3mxdm9', 'jxnvd5ne', '25n8m97p', 'x7gpxjpr', 'xpvdwdvp'].indexOf(entityHash) !== -1;

export const GA_BETA_OPT_IN = 'ga_beta_opt_in';
export const GA_R1 = 'ga_r1';

export const setCookie = (name, value, days) => {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value}${expires}; path=/`;
};

export const getCookie = name => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export function isIE(nav: Navigator): boolean {
  return nav.userAgent.indexOf('MSIE') != -1 || !!navigator.userAgent.match(/Trident\/7\./);
}

export function isWindows(nav: Navigator): boolean {
  return nav.userAgent.indexOf('Windows') != -1;
}

export function getDocumentStatusClass(documentStatus: string) {
  switch (documentStatus) {
    case 'draft':
      return 'ga-default';

    case 'hardbounced':
    case 'partlyrejected':
    case 'rejected':
    case 'recalled':
    case 'lost':
    case 'expired':
      return 'ga-danger';

    case 'deferred':
    case 'reviewed':
    case 'viewed':
    case 'commented':
    case 'clicked':
    case 'downloaded':
    case 'attachment':
    case 'attachmentopened':
    case 'waitingforsignature':
    case 'partlysigned':
      return 'ga-warning';

    case 'sent':
    case 'sms':
    case 'reminder':
    case 'video':
    case 'paused':
    case 'updated':
      return 'ga-primary';

    case 'processing':
    case 'importing':
    case 'printed':
    case 'created':
    case 'sealed':
    case 'scheduled':
    case 'waiting':
    case 'archived':
      return 'ga-info';

    case 'delivered':
    case 'approved':
    case 'verified':
    case 'signed':
      return 'ga-success';

    case 'signedwithoutverification':
      return 'ga-success unverified';

    default:
      return 'ga-default';
  }
}

export function capitalizeFirstLetter(string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function inProduction(currentWindow: Window): boolean {
  return currentWindow.location.href.includes('getaccept.com');
}

export function isPaidPlan(session: ISession): boolean {
  switch (session.plan) {
    // case EnumPlans.trial: We have decided that trial plan is something that is manually enabled from our sales department. They need to be able to access paid stuff such as branding and teams.
    case EnumPlans.free:
    case EnumPlans.solo:
      return false;
    default:
      return true;
  }
}

export function getBinaryPayloadValue(isSet: boolean): string {
  return isSet ? '1' : '0';
}

export function isLegacyPlan(plan: EnumPlans): boolean {
  switch (plan) {
    case EnumPlans.trial:
    case EnumPlans.solo:
    case EnumPlans.starter:
    case EnumPlans.business:
    case EnumPlans.pro:
    case EnumPlans.enterprise:
      return true;
    default:
      return false;
  }
}

export function numToWord(num: number): string {
  switch (num) {
    case 1:
      return translate('one');
    case 2:
      return translate('two');
    case 3:
      return translate('three');
    case 4:
      return translate('four');
    case 5:
      return translate('five');
    case 6:
      return translate('six');
    case 7:
      return translate('seven');
    case 8:
      return translate('eight');
    case 9:
      return translate('nine');
    case 10:
      return translate('ten');
    default:
      return String(num);
  }
}
